// ---------------------------------------------------------------------------
// The browser side of ANALYTICS AND THE DELIVERY SCORECARD. V33.
//
// NO ARITHMETIC HERE OR IN THE SCREEN. Every rate arrives as basis points
// already computed by `app.delivery_scorecard`, and `percent` from
// `system/format.ts` — the one module in this application allowed to divide —
// puts it on the page. `verify-V33.sh` greps this directory for the operators
// after stripping comments.
//
// A fill rate divided in the browser would be a SECOND metric engine, and the
// whole argument of this slice is that a second source of a number is a number
// that will start disagreeing with the first one and never say so.
//
// AND EVERY ROW CARRIES ITS OWN DISAGREEMENT COLUMN. `agrees` is not a debug
// field: it is the answer to "should I believe this", and a screen that dropped
// it would be showing a figure whose provenance it had thrown away.
// ---------------------------------------------------------------------------

import { read } from "./console";

export type ScorecardMetric = {
  metric_id: string;
  label: string;
  unit: string;
  /** S70 (0203). How a figure in this unit is printed, from `app.metric_unit`. */
  unit_is_percent: boolean;
  unit_suffix: string | null;
  rank: number;
  /** `live_against_journal` or `journal_only`. The second is not a weaker
   *  metric — it is one nothing in the live tables records at all, which is
   *  precisely why it must never become a stored column. */
  reconciles_as: string;
  live_numerator: string | null;
  live_denominator: string | null;
  live_value: string | null;
  journal_numerator: string | null;
  journal_denominator: string | null;
  journal_value: string | null;
  /** Null where there is no live counterpart to disagree with. */
  agrees: boolean | null;
  is_measurable: boolean;
  /** The database's own sentence. A metric with no data is dropped, never
   *  scored zero — a no-show rate of zero and no shifts having ended yet are
   *  opposite facts and the second one reads as a perfect record. */
  not_measurable_reason: string | null;
};

export type ClientDelivery = {
  client_account_id: string;
  client_name: string;
  month_start: string;
  seats: string;
  filled_live: string;
  filled_journal: string;
  fill_basis_points: string | null;
  ended: string;
  absences: string;
  no_show_basis_points: string | null;
  agrees: boolean;
};

export type ReconciliationCheck = {
  check_id: string;
  /** ★ WHICH TWO RECORDS THIS ROW COMPARES, in the database's own words. Three
   *  different checks come back in one list and they compare three different
   *  pairs; without this the screen has to guess, and the first version guessed
   *  wrong in the only direction that matters. */
  compares: string;
  subject: string;
  first_says: string;
  second_says: string;
  reconciles: boolean;
};

export type CounterRegisterRow = {
  relation: string;
  column_name: string;
  is_metric_safe: boolean;
  counted_relation: string | null;
  reason: string;
};

export type CounterDriftRow = {
  relation: string;
  column_name: string;
  subject_id: string;
  stored: string;
  live: string;
};

export type CounterDefect = { system_id: string; defect: string };

export const analytics = {
  scorecard: (base: string, from: string, to: string, clientId?: string | null) =>
    read(
      base,
      `/v1/analytics/scorecard?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` +
        (clientId ? `&client_id=${encodeURIComponent(clientId)}` : ""),
    ),
  clients: (base: string, from: string, to: string) =>
    read(
      base,
      `/v1/analytics/clients?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  reconciliation: (base: string, from: string, to: string) =>
    read(
      base,
      `/v1/analytics/reconciliation?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  counters: (base: string) => read(base, "/v1/analytics/counters"),
};
